import { useRef, useState } from "react";
import { playSfx } from "../sound/sfx";

/*
 * «تأیید قبل از بازی کارت» — LIFT A CARD, THEN TAP THE TABLE TO PLAY IT. حکم's and بی‌دل's hands share
 * this one rule; the Settings switch (`getLiftToPlay`, OFF by default) turns it on.
 *
 * Operator, 2026-10-08: «tap a card to lift it (tap it again to put it back), then tap the table to
 * play it — players say they sometimes play a card by mistake.»
 *
 * - THE LIFT IS DERIVED, NEVER STORED STALE. The hook keeps only {card, frame}; the lifted card is
 *   recomputed every render against the CURRENT frame and playable set, so it drops on any new
 *   server frame (the turn passed, the trick ended, a hand or phase change, a reconnect, a refused
 *   move) and the moment the card stops being playable. No effect keys on a frame object.
 * - A CARD TAP never plays. It lifts a playable card, puts the lifted one back, or switches to
 *   another playable card — SILENTLY: the card itself is `silent`, and the hook sounds nothing
 *   either. (The app's one light cue, «tap», follows the messages-sound switch, not the game-sound
 *   one, so a lift that ticked would sound with game sound OFF; ruling 2026-10-08, Task 1B.) One
 *   card is one «play» cue, on the table tap.
 * - A TABLE TAP plays the lifted card ONCE per frame (the table-level twin of `Card`'s commit
 *   latch: a weak connection can hold a move unconfirmed for seconds) with the «play» cue. Until the
 *   next frame, card taps change nothing — the move is in flight and the card stays up.
 * - A tap on a `<button>` inside the table (the bot seat's invite «+») is that button's, not the
 *   table's.
 * - INERT while the trick hold (`use-trick-hold.ts`, or پاسور's capture hold, `use-capture-hold.ts`)
 *   has the hand disabled — every send too, the one-tap `send(card)` included — and, in حکم and
 *   بی‌دل, while the setting is off — `lifted` is then always null and both taps do nothing (پاسور
 *   passes `enabled: true` and gates with `playable`/`retap`: its several-sets cards lift either way).
 * - پاسور (P1 Task 4, R4) rides the same hook for its capture picker (`pasur-capture.ts`): `playable`
 *   is what a tap LIFTS there (every legal card under the setting; a card with several capture sets
 *   whatever the setting — the choice is the player's), `onPlay` resolves the card to its move, and
 *   `retap` says what a second tap on the lifted card does — حکم's and بی‌دل's put it back (the
 *   default), and so does پاسور's with the setting ON (every card: the setting's rule); with the
 *   setting OFF the picker plays a settled capture, or only nudges an open choice. `play()` is the
 *   same once-per-frame send for a confirm button, and `inFlight` tells the table a send is waiting
 *   for its frame.
 */

/**
 * How much higher than a LEGAL card (its 18 px rise) the lifted one stands: 21 px in all — and,
 * since the other legal cards rest at 0 while it is up, 21 px above every neighbour.
 *
 * MEASURED, not chosen. The only room is the panel's 8 px `gap-2` between the fan box and the strip
 * row (the «حاکم» / ♥ points chips sit flush with that row's bottom). The ruling's +4 left 0.82 px
 * at the worst slot — the two cards of a 2-card hand, each tilted 1.3°, its far corner higher than
 * the centre card's — with the gold ring scaled with the card (2 × 1.15 px): under the 1 px margin
 * the layout probes hold every part to. +3 leaves 1.82 px (`lift-geometry.test.ts`, T6, sums it
 * from the fan's own constants; the Tier A probes measure it at every size). A bigger lift needs a
 * taller fan box. (Findings F9's +4 / 1.48 px counted the ring unscaled and the centre card only.)
 */
export const LIFTED_EXTRA_PX = 3;

/**
 * The lifted card's own transition: the fan's duration on an easing that never overshoots. On a
 * SWITCH the newly lifted card rises from 0 (it was resting) to its full height in one step, and the
 * fan's springy easing (`cubic-bezier(.175,.885,.32,1.275)`, ~8.7 % past each step) would carry it
 * ~2 px into the strip row for a few frames. The app's entrance easing peaks at exactly 1.
 */
export const LIFTED_TRANSITION = "transform .2s cubic-bezier(.2,.7,.3,1)";

/** What the hook remembers: which card was lifted, and in which server frame (`useFrameKey`). */
export interface LiftState {
  readonly card: string;
  readonly frame: number;
}

/** What the lifted card is derived against — the current render's view of the table. */
export interface LiftContext {
  /** The Settings switch — and, in بی‌دل, not passing (the pass keeps its own selecting); پاسور
   *  passes `true` and gates with `playable` / `retap` (P1 Task 4, R4). */
  readonly enabled: boolean;
  /** `useTrickHold(view).holding` (پاسور: `useCaptureHold(view).holding`): a finished trick — a
   *  capture — is on the felt and the hand is disabled. */
  readonly holding: boolean;
  /** `useFrameKey(view)` — bumps once per real server frame. */
  readonly frameKey: number;
  /** The cards a tap could play right now (the server's legal plays). */
  readonly playable: ReadonlySet<string>;
}

/** The card shown lifted this render, or null. Pure: same inputs, same answer. */
export function deriveLifted(state: LiftState | null, ctx: LiftContext): string | null {
  if (state === null || !ctx.enabled || ctx.holding) return null;
  return state.frame === ctx.frameKey && ctx.playable.has(state.card) ? state.card : null;
}

export interface LiftToPlayOptions extends LiftContext {
  /** Send the play — `(card) => onMove({ type: "play", card })`. Called at most once per frame. A
   *  caller that can find NO move for the card (پاسور's picker) answers `false`: then nothing was
   *  sent, so the hook arms no latch and sounds no cue — the next tap in the frame may still play.
   *  `void` (حکم's and بی‌دل's callers) means sent. */
  readonly onPlay: (card: string) => boolean | void;
  /** What a SECOND tap on the lifted card does: put it back (the default — حکم's and بی‌دل's, and
   *  پاسور's with the setting on), play it (the picker's settled capture, the setting off), or
   *  nothing but a nudge (its open choice, the setting off). */
  readonly retap?: (card: string) => "putBack" | "play" | "nudge";
}

/** What a card tap did — for the table to animate on («nudge»); the rest is in the render. */
export type LiftTap = "lift" | "switch" | "putBack" | "play" | "nudge" | "none";

export interface LiftToPlay {
  /** The lifted card: rise it `LIFTED_EXTRA_PX` over a legal card, ring it (`selected`), tag it. */
  readonly lifted: string | null;
  /** A hand card's `onClick` (give that `Card` `silent` and no `commitKey`). */
  readonly tapCard: (card: string) => LiftTap;
  /** The table's `onClick` — the playfield `<main>`. */
  readonly tapTable: (event: { readonly target: EventTarget | null }) => void;
  /** Play the lifted card now — the same once-per-frame send the table tap makes, for a confirm
   *  button of the table's own (پاسور's «جمع کن»). True when it sent. */
  readonly play: () => boolean;
  /** Play THIS card now, lifted or not — the picker's one-tap play of a card that never lifts (a
   *  trail or a lone capture with the setting off), through the same once-per-frame latch and the
   *  same one «play» cue, so no second card can follow it in the frame. True when it sent. */
  readonly send: (card: string) => boolean;
  /** A play was sent in this frame and its frame has not landed: taps change nothing meanwhile.
   *  A function, not a value: the latch is a ref the tap handlers write, and a handler fired after
   *  the send in the same render must read it live. */
  readonly inFlight: () => boolean;
}

/** Is this tap on (or inside) a `<button>` — its icon, the icon's path? (A tap on a button's TEXT
 *  reaches a React handler with the button itself as the target: React re-targets text nodes.) */
function onButton(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest("button") !== null;
}

export function useLiftToPlay(options: LiftToPlayOptions): LiftToPlay {
  const { enabled, holding, frameKey, playable, onPlay, retap } = options;
  const [state, setState] = useState<LiftState | null>(null);
  // The frame whose play this table already sent — read and written only in the tap handlers.
  const sentAt = useRef<number | null>(null);
  const lifted = deriveLifted(state, options);

  // THE one send: once per frame, with the one «play» cue, the lifted card staying up meanwhile.
  // The move is resolved FIRST: a caller that finds none answers false, and then neither the latch
  // nor the cue fires (a ghost «play» and a table dead until the next frame would be the cost).
  const send = (card: string): boolean => {
    // a hold has the hand disabled: the one-tap path honours it as every tap does (P1 Task 5 note 13
    // — `play()` already did, through `lifted`, which is null while holding)
    if (holding || sentAt.current === frameKey) return false;
    if (onPlay(card) === false) return false; // no move to send: no latch, no cue
    sentAt.current = frameKey;
    playSfx("play");
    return true;
  };
  const play = (): boolean => lifted !== null && send(lifted);

  const tapCard = (card: string): LiftTap => {
    if (!enabled || holding || sentAt.current === frameKey) return "none";
    if (card === lifted) {
      const what = retap ? retap(card) : "putBack";
      if (what === "putBack")
        setState(null); // put it back
      else if (what === "play") play();
      return what;
    }
    if (playable.has(card)) {
      const what: LiftTap = lifted === null ? "lift" : "switch";
      setState({ card, frame: frameKey }); // lift it, or switch to it
      return what;
    }
    return "none"; // not playable: nothing moves. Every branch is silent (see the header).
  };

  const tapTable = (event: { readonly target: EventTarget | null }): void => {
    if (onButton(event.target)) return;
    play();
  };

  return { lifted, tapCard, tapTable, play, send, inFlight: () => sentAt.current === frameKey };
}
